import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, getDaysInMonth } from 'date-fns';
import WheelPicker from './WheelPicker';

interface DatePickerProps {
    value: string; // YYYY-MM-DD
    onChange: (date: string) => void;
    placeholder?: string;
    className?: string;
    required?: boolean;
    size?: 'default' | 'admin';
}

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const DatePicker = ({
    value,
    onChange,
    placeholder = 'Select date',
    className = '',
    required = false,
    size = 'default',
}: DatePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Helper function to parse date string
    const parseDateValue = (dateStr: string) => {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Convert to 0-indexed
            const day = parseInt(parts[2], 10);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                return { day, month, year };
            }
        }
        return null;
    };

    // State for the picker - initialize from value prop directly
    const [selectedDay, setSelectedDay] = useState(() => {
        if (value) {
            const parsed = parseDateValue(value);
            if (parsed) return parsed.day;
        }
        return new Date().getDate();
    });
    const [selectedMonth, setSelectedMonth] = useState(() => {
        if (value) {
            const parsed = parseDateValue(value);
            if (parsed) return parsed.month;
        }
        return new Date().getMonth();
    });
    const [selectedYear, setSelectedYear] = useState(() => {
        if (value) {
            const parsed = parseDateValue(value);
            if (parsed) return parsed.year;
        }
        return new Date().getFullYear();
    });

    // Sync state when value prop changes externally
    useEffect(() => {
        if (value) {
            const parsed = parseDateValue(value);
            if (parsed) {
                setSelectedDay(parsed.day);
                setSelectedMonth(parsed.month);
                setSelectedYear(parsed.year);
            }
        } else {
            const now = new Date();
            setSelectedDay(now.getDate());
            setSelectedMonth(now.getMonth());
            setSelectedYear(now.getFullYear());
        }
    }, [value]);

    // Close on click outside
    useEffect(() => {
        if (isOpen) {
            const closeListener = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (!target.closest('[data-datepicker-portal]') && !containerRef.current?.contains(target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', closeListener);
            return () => document.removeEventListener('mousedown', closeListener);
        }
    }, [isOpen]);

    const handleWheelChange = (type: 'day' | 'month' | 'year', val: number) => {
        let d = selectedDay;
        let m = selectedMonth;
        let y = selectedYear;

        if (type === 'day') d = val;
        if (type === 'month') m = val;
        if (type === 'year') y = val;

        // Validate day (e.g. going from Mar 31 to Feb -> Feb 28/29)
        const daysInMonth = getDaysInMonth(new Date(y, m));
        if (d > daysInMonth) {
            d = daysInMonth;
        }

        setSelectedDay(d);
        setSelectedMonth(m);
        setSelectedYear(y);

        // Don't call onChange here - only update local state
        // The change will be committed when user clicks "Done"
    };

    // Commit the selected date to parent
    const handleDone = () => {
        const newDate = new Date(selectedYear, selectedMonth, selectedDay);
        onChange(format(newDate, 'yyyy-MM-dd'));
        setIsOpen(false);
    };

    // Prepare Options
    const daysInCurrentMonth = getDaysInMonth(new Date(selectedYear, selectedMonth));
    const dayOptions = Array.from({ length: daysInCurrentMonth }, (_, i) => ({
        label: (i + 1).toString(),
        value: i + 1
    }));

    const monthOptions = MONTHS.map((name, index) => ({
        label: name,
        value: index
    }));

    const currentYear = new Date().getFullYear();
    const startYear = 1900;
    const endYear = currentYear + 100;
    const yearOptions = Array.from({ length: endYear - startYear + 1 }, (_, i) => {
        const y = startYear + i;
        return { label: y.toString(), value: y };
    }).reverse(); // Recent years first usually better? user image shows ascending. Let's do ascending for scrolling logic consistency? 
    // Actually lists usually are chronological top-down or bottom-up.
    // Let's keep ascending (1900 -> 2100) or Reverse (2100 -> 1900).
    // The previous implementation had reverse.
    // Standard iOS picker is often infinite or ascending. 
    // Let's do Reverse so 2024 is near the top if we default there.
    // Actually, WheelPicker scrolls to the value. So order validation is handled by 'value'.
    // Let's do Ascending (1900 at top) so scrolling down increases year.

    const yearOptionsAsc = Array.from({ length: endYear - startYear + 1 }, (_, i) => {
        const y = startYear + i;
        return { label: y.toString(), value: y };
    });

    // Parse date string safely to avoid timezone issues
    // new Date("2026-01-08") interprets as UTC, which can shift days in local timezone
    const getLocalDate = (dateStr: string) => {
        const parsed = parseDateValue(dateStr);
        if (parsed) {
            return new Date(parsed.year, parsed.month, parsed.day);
        }
        return new Date();
    };

    const displayValue = value ? format(getLocalDate(value), 'dd MMM yyyy') : '';

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="relative">
                <input
                    type="text"
                    readOnly
                    value={displayValue}
                    placeholder={placeholder}
                    required={required}
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full bg-white/5 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent transition-all cursor-pointer font-sans ${
                        size === 'admin'
                            ? 'h-9 pl-3 pr-8 rounded-xl text-[11px] placeholder:text-[11px] placeholder-white/30'
                            : 'py-3 md:py-4 pl-4 pr-12 rounded-2xl text-sm md:text-base placeholder-white/30'
                    }`}
                    // Prevent soft keyboard
                    inputMode="none"
                />

                <div
                    className={`absolute top-1/2 -translate-y-1/2 text-primary-400 cursor-pointer ${size === 'admin' ? 'right-2.5' : 'right-4'}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <CalendarIcon size={size === 'admin' ? 14 : 18} />
                </div>
            </div>

            {isOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Modal */}
                    <div
                        data-datepicker-portal
                        className="relative bg-cosmic-900/40 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl p-6 w-80"
                    >
                        <div className="text-center text-purple-200 mb-4 font-bold uppercase text-xs tracking-wider">Select Date</div>

                        {/* Today Button - scrolls wheels to today without closing */}
                        <button
                            type="button"
                            onClick={() => {
                                const today = new Date();
                                setSelectedDay(today.getDate());
                                setSelectedMonth(today.getMonth());
                                setSelectedYear(today.getFullYear());
                                // Don't close - let user click Done to confirm
                            }}
                            className="w-full mb-4 py-2 bg-white/5 hover:bg-white/10 border border-purple-400/30 hover:border-purple-400/50 rounded-lg text-purple-200 hover:text-white font-medium text-sm transition-all"
                        >
                            Today
                        </button>

                        <div className="flex gap-2 items-center justify-center h-[220px]">
                            {/* Day */}
                            <div className="w-16 flex-shrink-0">
                                <WheelPicker
                                    options={dayOptions}
                                    value={selectedDay}
                                    onChange={(v) => handleWheelChange('day', v as number)}
                                />
                            </div>

                            {/* Month */}
                            <div className="w-20 flex-shrink-0">
                                <WheelPicker
                                    options={monthOptions}
                                    value={selectedMonth}
                                    onChange={(v) => handleWheelChange('month', v as number)}
                                />
                            </div>

                            {/* Year */}
                            <div className="w-20 flex-shrink-0">
                                <WheelPicker
                                    options={yearOptionsAsc}
                                    value={selectedYear}
                                    onChange={(v) => handleWheelChange('year', v as number)}
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleDone}
                            className="w-full mt-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl text-white font-bold text-sm transition-colors"
                        >
                            Done
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DatePicker;
